import { produce } from 'immer';
import {
  mutateRemoveContainer,
  mutateRemoveContainers,
  mutateAddContainers,
  mutateUpdateContainer,
  mutateRemmoveSectionContainers,
  mutateDropInContainer,
  newContainer,
} from '../utils/containers';
import {
  computeColumnWidth,
  containerColumnGap,
  getColumnAvailableWidth,
  getTopLevelContainer,
  mutateAddColumns,
  mutateDeleteColumns,
  mutateRemoveRowContainers,
  newColumn,
} from '../utils/columns';
import {
  createBoxesFromPdfToImage,
  initializeBoxContent,
  isATitleBox,
  mutateAddBoxes,
  mutateRemoveBox,
  prepareBoxesForContainerPaste,
  reinitializeBoxWidthAndHeight,
} from '../utils/boxes';
import { gridUnit } from '../constants/gridConfig';
import { ItemTypes, MASKS_TYPE, uuid } from '../constants/constants';
import { getContainerTemplate } from '../constants/containerTemplates';
import { getColumnById, getContainerByColumnId } from './lookups';
import { EditorState } from 'draft-js';
import { message } from 'antd';
import {
  containerLockHolder,
  columnsLockHolder,
  sectionLoadingHolder,
  editFreezeHolder,
  previewContainerLockHolder,
  previewColumnsLockHolder,
} from '../collab/structureLocks';

// Collab : refuse l'opération (toast) si le container héberge une box éditée
// par un co-éditeur, ou si une injection de section est en cours ailleurs.
// Retourne true si bloqué.
const denyIfContainerLocked = (state, containerIds) => {
  const sectionLoader = sectionLoadingHolder(state);
  if (sectionLoader) {
    message.warning(`${sectionLoader.name} charge une section, structure gelée un instant`);
    return true;
  }
  for (const containerId of [].concat(containerIds)) {
    const holder = containerLockHolder(state, containerId);
    if (holder) {
      message.warning(`Conteneur verrouillé : ${holder.name} édite un bloc dedans`);
      return true;
    }
    const previewHolder = previewContainerLockHolder(state, containerId);
    if (previewHolder) {
      message.warning(`Section en aperçu par ${previewHolder.name}`);
      return true;
    }
  }
  return false;
};

// Même verrou que denyIfContainerLocked, à partir d'une colonne : les lignes
// imbriquées n'ont pas de verrou propre, c'est celui du container top-level
// qui les héberge qui fait foi.
const denyIfColumnLocked = (state, columnId) => {
  const topLevel = getTopLevelContainer({
    columnId,
    containers: state.containers,
    rowContainers: state.rowContainers,
  });
  return topLevel ? denyIfContainerLocked(state, topLevel.id) : false;
};

// Le contenu d'une section liée appartient à sa source et se réinjecte à chaque
// chargement : on n'y ajoute ni n'y retire de ligne, comme on n'y ajoute pas de
// colonne (cf. AdderColumns). Sans ça, la ligne ajoutée disloquait le container
// et disparaissait à la réinjection.
const denyIfLinkedSection = (state, columnId) =>
  !!getTopLevelContainer({
    columnId,
    containers: state.containers,
    rowContainers: state.rowContainers,
  })?.from_linked_section;

// Crée une ligne (container imbriqué) et ses sous-colonnes, sans la rattacher
// à la colonne parente — c'est à l'appelant de l'insérer dans rowContainerIds.
const createRowInColumn = (draft, { parentColumnId, columnsSizes }) => {
  const columns = columnsSizes.map((size) => newColumn({ size }));
  const row = newContainer({
    columnsIds: columns.map((c) => c.id),
    editableConfiguration:
      draft.currentProposal?.id !== null
        ? true
        : draft.configuration.editableTemplate,
  });
  row.parentColumnId = parentColumnId;
  mutateAddColumns(draft, columns);
  // Réassignation (et non push) : les lookups de store/lookups.js cachent leurs
  // Map par référence de tableau, une mutation en place laisserait un cache
  // périmé pendant le même produce. Couvre aussi les documents chargés avant
  // l'introduction des lignes, où la clé peut manquer.
  draft.rowContainers = [...(draft.rowContainers ?? []), row];
  return { row, columns };
};

// Recale la largeur des boxes portées par une colonne, en descendant dans ses
// lignes le cas échéant : une colonne qui contient des lignes n'a pas de box
// en direct, ce sont les sous-colonnes qui en portent. Sans cette descente,
// redimensionner une colonne laissait ses boxes imbriquées à leur ancienne
// largeur, donc débordantes.
const mutateResizeBoxesUnderColumn = (draft, columnId) => {
  const column = draft.columns.find((c) => c.id === columnId);
  if (!column || column.drawMode) return;

  const rowIds = column.rowContainerIds ?? [];
  if (rowIds.length) {
    rowIds.forEach((rowId) => {
      const row = draft.rowContainers?.find((r) => r.id === rowId);
      row?.columnsIds.forEach((subColumnId) =>
        mutateResizeBoxesUnderColumn(draft, subColumnId)
      );
    });
    return;
  }

  const boxIdx = draft.boxes.findIndex((b) => b.columnId === columnId);
  if (boxIdx === -1) return;
  const columnWidth = getColumnAvailableWidth({
    columnId,
    columns: draft.columns,
    containers: draft.containers,
    rowContainers: draft.rowContainers,
    landscape: draft.landscape,
    marginLeft: draft.configuration.margins.left * gridUnit,
    marginRight: draft.configuration.margins.right * gridUnit,
  });
  // getColumnAvailableWidth rend 0 quand la colonne n'est rattachée à aucun
  // container ni ligne : mieux vaut laisser la box telle quelle que la réduire
  // à néant.
  if (columnWidth <= 0) return;
  draft.boxes[boxIdx] = reinitializeBoxWidthAndHeight({
    header: draft.masks.find(({ id }) => id === MASKS_TYPE.HEADER.id),
    footer: draft.masks.find(({ id }) => id === MASKS_TYPE.FOOTER.id),
    box: draft.boxes[boxIdx],
    columnWidth,
  });
};

// Ajuste les colonnes d'un container ou d'une ligne pour coller exactement à
// `columnsSizes` : ajoute, retire (avec leur contenu) et redimensionne.
const mutateApplyColumnSizes = (draft, { containerId, columnsSizes }) => {
  const container =
    draft.containers.find((c) => c.id === containerId) ??
    draft.rowContainers?.find((c) => c.id === containerId);
  if (!container || !columnsSizes.length) return;

  if (container.columnsIds.length > columnsSizes.length) {
    const removedIds = container.columnsIds.slice(columnsSizes.length);
    container.columnsIds = container.columnsIds.slice(0, columnsSizes.length);
    mutateDeleteColumns(draft, removedIds);
  }
  while (container.columnsIds.length < columnsSizes.length) {
    const addedColumn = newColumn({ size: 1 });
    mutateAddColumns(draft, [addedColumn]);
    container.columnsIds.push(addedColumn.id);
  }

  // Toutes les tailles d'abord : le recalcul des largeurs qui suit remonte la
  // chaîne des parents et doit la voir dans son état final.
  // Mutation directe plutôt que mutateUpdateColumn : celui-ci réassigne
  // draft.columns en y replaçant des drafts, et l'appeler en boucle laisse des
  // proxies révoqués dans le tableau final (cf. rowContainerIds, qui est un
  // tableau et non une primitive).
  container.columnsIds.forEach((cId, idx) => {
    const column = draft.columns.find((c) => c.id === cId);
    if (column) column.size = columnsSizes[idx];
  });
  container.columnsIds.forEach((cId) =>
    mutateResizeBoxesUnderColumn(draft, cId)
  );
};

// Ajuste le nombre de lignes empilées d'une colonne. rowCount <= 1 ramène la
// colonne à une colonne simple (la première box trouvée dans les lignes est
// remontée, le reste part avec les lignes).
const mutateSetColumnRows = (draft, { columnId, rowCount }) => {
  const column = draft.columns.find((c) => c.id === columnId);
  if (!column || column.drawMode) return;
  const currentRowIds = column.rowContainerIds ?? [];

  if (rowCount <= 1) {
    if (!currentRowIds.length) {
      mutateResizeBoxesUnderColumn(draft, columnId);
      return;
    }
    const nestedColumnIds = new Set(
      currentRowIds.flatMap(
        (rowId) =>
          draft.rowContainers.find((r) => r.id === rowId)?.columnsIds ?? []
      )
    );
    const boxToKeep = draft.boxes.find((b) => nestedColumnIds.has(b.columnId));
    if (boxToKeep) boxToKeep.columnId = columnId;
    column.rowContainerIds = [];
    mutateRemoveRowContainers(draft, currentRowIds);
    mutateResizeBoxesUnderColumn(draft, columnId);
    return;
  }

  if (!currentRowIds.length) {
    const existingBox = draft.boxes.find((b) => b.columnId === columnId);
    const { row, columns } = createRowInColumn(draft, {
      parentColumnId: columnId,
      columnsSizes: [1],
    });
    if (existingBox) existingBox.columnId = columns[0].id;
    column.rowContainerIds = [row.id];
  }
  while (column.rowContainerIds.length < rowCount) {
    const { row } = createRowInColumn(draft, {
      parentColumnId: columnId,
      columnsSizes: [1],
    });
    column.rowContainerIds.push(row.id);
  }
  if (column.rowContainerIds.length > rowCount) {
    const removedRowIds = column.rowContainerIds.slice(rowCount);
    column.rowContainerIds = column.rowContainerIds.slice(0, rowCount);
    mutateRemoveRowContainers(draft, removedRowIds);
  }
  // Les boxes ont changé de niveau (colonne ↔ sous-colonne) : leur largeur de
  // référence n'est plus la même.
  mutateResizeBoxesUnderColumn(draft, columnId);
};

// Instancie une disposition (cf. constants/containerTemplates.js) : la grille
// entière — colonnes, lignes imbriquées, sous-colonnes — et son contenu, en un
// seul passage.
//
// Un seul mutateur plutôt qu'une composition d'actions du store (addContainers
// puis applyContainerLayout puis N ajouts de box) : chaque action est un `set`,
// donc une entrée d'historique, un rendu et un diff collab. Poser une
// disposition doit coûter un seul « annuler ».
// Colle un container copié : sa grille complète (colonnes, lignes imbriquées,
// sous-colonnes) puis ses box, chacune reposée dans la colonne d'où elle vient
// (cf. `columnPath`, posé par CopyContainer). Le collage ne passait que par
// mutateAddContainers, qui ne connaît qu'un niveau de colonnes : un container à
// plusieurs lignes perdait sa grille et son contenu imbriqué.
//
// Un seul mutateur, comme pour les dispositions : une entrée d'historique, un
// rendu, un diff collab.
const mutateAddCopiedContainer = (draft, { index, copy }) => {
  const layout = copy?.layout?.length
    ? copy.layout
    : copy?.columnsDisposition?.length
      ? copy.columnsDisposition
      : [{ size: 1 }];
  const drawMode = !!copy?.drawMode;

  const existingIds = new Set(draft.containers.map((c) => c.id));
  mutateAddContainers({
    draft,
    index,
    columnsSizes: drawMode ? [1] : layout.map((column) => column.size ?? 1),
    drawMode,
    fromCopiedContainer: {
      container_height: copy?.copiedContainer?.height,
      maskId: copy?.maskId,
    },
    shouldSelect: false,
  });
  const container = draft.containers.find((c) => !existingIds.has(c.id));
  if (!container) return;
  // Avant le recalage des largeurs en fin de collage : elles dépendent de
  // l'écart entre colonnes.
  const { lineSpacing, columnGap } = copy?.copiedContainer ?? {};
  if (lineSpacing != null) container.lineSpacing = lineSpacing;
  if (columnGap != null) container.columnGap = columnGap;

  // Recrée les lignes imbriquées et retient l'id de colonne posé à chaque
  // chemin, pour y remettre les box ensuite.
  const columnIdByPath = new Map();
  const buildColumn = (columnId, descriptor, path) => {
    columnIdByPath.set(path.join('-'), columnId);
    const column = draft.columns.find((c) => c.id === columnId);
    // Répartition verticale des box de la cellule : réglage de mise en page, il
    // suit la copie comme la taille de la colonne.
    if (column && descriptor?.verticalAlign)
      column.verticalAlign = descriptor.verticalAlign;
    const rows = descriptor?.rows ?? [];
    if (!rows.length) return;
    if (!column) return;
    column.rowContainerIds = [];
    rows.forEach((rowDescriptor, rowIdx) => {
      const rowColumns = rowDescriptor.columns?.length
        ? rowDescriptor.columns
        : [{ size: 1 }];
      const { row, columns } = createRowInColumn(draft, {
        parentColumnId: columnId,
        columnsSizes: rowColumns.map((c) => c.size ?? 1),
      });
      column.rowContainerIds.push(row.id);
      columns.forEach((subColumn, subIdx) =>
        buildColumn(subColumn.id, rowColumns[subIdx], [...path, rowIdx, subIdx])
      );
    });
  };

  if (!drawMode) {
    container.columnsIds.forEach((columnId, idx) =>
      buildColumn(columnId, layout[idx], [idx])
    );
  }

  const titles = draft.boxes.filter((b) => isATitleBox(b.type));
  const preparedBoxes = prepareBoxesForContainerPaste({
    boxes: copy?.boxes ?? [],
    configuration: draft.configuration,
    titles,
  });

  const newBoxes = [];
  preparedBoxes.forEach((box, idx) => {
    // Copie d'avant `columnPath` : on retombe sur l'ancienne règle, une box par
    // colonne dans l'ordre.
    const path = box.columnPath ?? [drawMode ? 0 : idx];
    delete box.columnPath;
    const columnId = drawMode
      ? container.columnsIds[0]
      : columnIdByPath.get(path.join('-'));
    if (!columnId) return;
    newBoxes.push({ ...box, columnId });
  });

  if (newBoxes.length) mutateAddBoxes({ draft, newBoxes });

  // Largeurs en dernier : une box de sous-colonne se dimensionne d'après sa
  // colonne parente, connue seulement une fois la grille entière posée.
  container.columnsIds.forEach((columnId) =>
    mutateResizeBoxesUnderColumn(draft, columnId)
  );

  mutateDropInContainer({ draft, container });
};

const mutateAddContainerFromTemplate = (draft, { template, index }) => {
  if (!template?.columns?.length) return;

  // mutateAddContainers ne rend pas le container créé : on le retrouve comme
  // le seul dont l'id n'existait pas avant.
  const existingIds = new Set(draft.containers.map((c) => c.id));
  mutateAddContainers({
    draft,
    index,
    columnsSizes: template.columns.map((c) => c.size),
    shouldSelect: false,
  });
  const container = draft.containers.find((c) => !existingIds.has(c.id));
  if (!container) return;

  const titles = draft.boxes.filter((b) => isATitleBox(b.type));
  const newBoxes = [];

  const buildColumn = (columnId, descriptor) => {
    // Feuille : la colonne porte la box décrite, s'il y en a une (les
    // dispositions de catégorie 'grid' n'en ont aucune).
    if (!descriptor?.rows?.length) {
      if (!descriptor?.box?.type) return;
      newBoxes.push(
        initializeBoxContent({
          box: {
            type: descriptor.box.type,
            ...draft.gridConfiguration[descriptor.box.type],
            copied: false,
          },
          configuration: draft.configuration,
          titles,
          column: true,
          parentId: columnId,
        })
      );
      return;
    }

    const column = draft.columns.find((c) => c.id === columnId);
    if (!column) return;
    column.rowContainerIds = [];
    descriptor.rows.forEach((rowDescriptor) => {
      const rowColumns = rowDescriptor.columns?.length
        ? rowDescriptor.columns
        : [{ size: 1 }];
      const { row, columns } = createRowInColumn(draft, {
        parentColumnId: columnId,
        columnsSizes: rowColumns.map((c) => c.size ?? 1),
      });
      column.rowContainerIds.push(row.id);
      columns.forEach((subColumn, idx) =>
        buildColumn(subColumn.id, rowColumns[idx])
      );
    });
  };

  container.columnsIds.forEach((columnId, idx) =>
    buildColumn(columnId, template.columns[idx])
  );

  // Un seul ajout groupé : mutateAddBoxes réindexe les titres et recalcule le
  // sommaire à chaque appel.
  if (newBoxes.length) mutateAddBoxes({ draft, newBoxes });
  // Largeurs en dernier : une box de sous-colonne se dimensionne d'après la
  // largeur de sa colonne parente, qui n'est connue qu'une fois toute la
  // grille posée.
  container.columnsIds.forEach((columnId) =>
    mutateResizeBoxesUnderColumn(draft, columnId)
  );

  mutateDropInContainer({ draft, container });
};

const sanitizeSelectedContainerIds = (draft, selectedIds = []) => {
  const existingContainerIds = new Set(draft.containers.map((c) => c.id));
  return [...new Set(selectedIds)].filter((id) => existingContainerIds.has(id));
};

const setContainerSelection = (
  draft,
  { selectedIds = [], activeId = null }
) => {
  const sanitizedIds = sanitizeSelectedContainerIds(draft, selectedIds);
  const safeActiveId =
    activeId && sanitizedIds.includes(activeId)
      ? activeId
      : sanitizedIds[0] || null;
  draft.selectedContainerIds = sanitizedIds;
  draft.activeContainerId = safeActiveId;
  draft.selectedContainer = safeActiveId
    ? draft.containers.find((c) => c.id === safeActiveId) || null
    : null;
};

const getLinkedSectionGroupContainerIds = (draft, container) => {
  if (!container) return [];
  const isTemplate = draft.currentDocument?.type === 'template';
  if (!isTemplate || !container.from_linked_section) {
    return [container.id];
  }
  return draft.containers
    .filter(
      (c) =>
        c.sectionId === container.sectionId &&
        c.dateTimestamp === container.dateTimestamp
    )
    .map((c) => c.id);
};

const getRangeContainerIds = (draft, startId, endId) => {
  const startIndex = draft.containers.findIndex((c) => c.id === startId);
  const endIndex = draft.containers.findIndex((c) => c.id === endId);
  if (startIndex === -1 || endIndex === -1) return [];
  const [minIndex, maxIndex] =
    startIndex <= endIndex ? [startIndex, endIndex] : [endIndex, startIndex];
  return draft.containers.slice(minIndex, maxIndex + 1).map((c) => c.id);
};

export const containerActions = (set, get) => ({
  /* CONTAINERS */
  addContainers: ({
    index,
    columnsSizes = [1],
    count = 1,
    drawMode = false,
    box,
    from = null,
    fromCopiedContainer = false,
    shouldSelect = true,
    callback,
  }) => {
    // Collab : un co-éditeur configure ou charge une section → création gelée
    const freeze = editFreezeHolder(get());
    if (freeze) {
      message.warning(freeze.message);
      return;
    }
    set(
      produce((draft) => {
        mutateAddContainers({
          draft,
          index,
          count,
          columnsSizes,
          drawMode,
          box,
          from,
          fromCopiedContainer,
          shouldSelect: shouldSelect && !draft.previewRenderState,
        });
      }),
      false,
      `add ${count} new Containers`
    );
    if (callback) callback();
  },
  /**
   * Colle un container copié à l'index donné : sa grille complète — colonnes,
   * lignes imbriquées, sous-colonnes — et ses box à leur place d'origine.
   * L'item du presse-papiers suffit, la préparation des box se fait ici (le
   * store a déjà la configuration et les titres).
   */
  addCopiedContainer: ({ index, copy, callback }) => {
    const freeze = editFreezeHolder(get());
    if (freeze) {
      message.warning(freeze.message);
      return;
    }
    set(
      produce((draft) => {
        mutateAddCopiedContainer(draft, { index, copy });
      }),
      false,
      'addCopiedContainer'
    );
    if (callback) callback();
  },
  /**
   * Pose une disposition prête à l'emploi (cf. constants/containerTemplates.js)
   * à l'index donné. `templateId` et non la disposition elle-même : l'appelant
   * peut être un item de glisser-déposer, qui doit rester minuscule et ne pas
   * figer une copie du catalogue au moment où le drag commence.
   */
  addContainerFromTemplate: ({ templateId, index }) => {
    // Même gel collab que addContainers : une création pendant qu'un
    // co-éditeur charge une section laisserait deux structures divergentes.
    const freeze = editFreezeHolder(get());
    if (freeze) {
      message.warning(freeze.message);
      return;
    }
    const template = getContainerTemplate(templateId);
    if (!template) return;
    set(
      produce((draft) => {
        mutateAddContainerFromTemplate(draft, { template, index });
      }),
      false,
      `addContainerFromTemplate ${templateId}`
    );
  },
  removeContainer: (containerId, callback) => {
    if (denyIfContainerLocked(get(), containerId)) return;
    set(
      produce((draft) => {
        mutateRemoveContainer(draft, containerId);
        const selectedBoxStillExists = [
          ...draft.boxes,
          ...draft.masks.flatMap((m) => m.boxes ?? []),
        ].some((b) => b.id === draft.selectedBoxId);
        if (!selectedBoxStillExists) draft.selectedBoxId = null;
        setContainerSelection(draft, {
          selectedIds: draft.selectedContainerIds?.filter(
            (id) => id !== containerId
          ),
          activeId:
            draft.activeContainerId === containerId
              ? null
              : draft.activeContainerId,
        });
      }),
      false,
      `remove Container ${containerId}`
    );
    if (callback) callback();
  },
  deleteColumns: (columnsIds) => {
    const holder = columnsLockHolder(get(), columnsIds);
    if (holder) {
      message.warning(`Conteneur verrouillé : ${holder.name} édite un bloc dedans`);
      return;
    }
    const previewHolder = previewColumnsLockHolder(get(), columnsIds);
    if (previewHolder) {
      message.warning(`Section en aperçu par ${previewHolder.name}`);
      return;
    }
    set(
      produce((draft) => {
        mutateDeleteColumns(draft, columnsIds);
      }),
      false,
      `delete ${columnsIds.length} columns`
    );
  },
  setTempContainerPdfToImage: ({ id, drawMode, boxId, fileName }) => {
    set(
      produce((draft) => {
        draft.tempContainerPdfToImage = { id, drawMode };
        const container = draft.containers.find((c) => c.id === id);
        const box = draft.boxes.find((b) => b.id === boxId);
        box.fileName = fileName;
        if (
          box.type === ItemTypes.FILE_PDF_VARIABLE &&
          !!container.KeyPdfToImageParent
        ) {
          const containersToDelete = draft.containers.filter(
            (c) => c.KeyPdfToImage === container.KeyPdfToImageParent
          );
          containersToDelete.forEach((c) => {
            mutateRemoveContainer(draft, c.id);
          });
        }
      }),
      false,
      `containerPdfToImage`
    );
  },
  removePdfContainer: () => {
    set(
      produce((draft) => {
        const container = draft.containers.find(
          (c) => c.id === draft.tempContainerPdfToImage.id
        );
        const box = draft.boxes.find((b) =>
          container.columnsIds.includes(b.columnId)
        );
        if (box.type === ItemTypes.FILE_PDF_VARIABLE) {
          return;
        }
        mutateRemoveContainer(draft, draft.tempContainerPdfToImage.id);
      }),
      false,
      `removeContainerPdfToImage`
    );
  },
  addContainerPdfToImage: ({ data }) => {
    set(
      produce((draft) => {
        createBoxesFromPdfToImage({
          data,
          draft,
        });
      }),
      false,
      `Create Container from PDF`
    );
  },
  handleSelectContainer: (payload) => {
    set(
      produce((draft) => {
        const normalizedPayload =
          typeof payload === 'string'
            ? { containerId: payload }
            : payload || {};
        const {
          containerId,
          ctrlKey = false,
          metaKey = false,
          shiftKey = false,
          fromBox = false,
          fromBoxClick = false,
        } = normalizedPayload;
        const container = draft.containers.find((c) => c.id === containerId);
        if (!container) return;

        // Cas 5/6 : si on part d'une box sélectionnée (sans container déjà sélectionné),
        // la sélection multiple doit prendre comme base le container de cette box.
        const selectedBox = draft.selectedBoxId
          ? draft.boxes.find((b) => b.id === draft.selectedBoxId)
          : null;
        const selectedBoxContainerId = selectedBox
          ? draft.containers.find((c) =>
              c.columnsIds.includes(selectedBox.columnId)
            )?.id
          : null;
        const seedSelectionIds =
          (ctrlKey || metaKey || shiftKey) &&
          !draft.selectedContainerIds?.length &&
          !draft.selectedContainer?.id &&
          selectedBoxContainerId
            ? [selectedBoxContainerId]
            : [];

        const baseSelectedIds =
          draft.selectedContainerIds?.length > 0
            ? draft.selectedContainerIds
            : draft.selectedContainer?.id
              ? [draft.selectedContainer.id]
              : seedSelectionIds;

        const isToggleMode = ctrlKey || metaKey;
        let nextSelectedIds = [];

        if (shiftKey) {
          const anchorId =
            draft.activeContainerId ||
            baseSelectedIds[0] ||
            selectedBoxContainerId ||
            containerId;
          const rangeIds = getRangeContainerIds(draft, anchorId, containerId);
          nextSelectedIds = isToggleMode
            ? [...new Set([...baseSelectedIds, ...rangeIds])]
            : rangeIds;
        } else if (isToggleMode) {
          const targetIds = getLinkedSectionGroupContainerIds(draft, container);
          const isWholeGroupSelected = targetIds.every((id) =>
            baseSelectedIds.includes(id)
          );
          nextSelectedIds = isWholeGroupSelected
            ? baseSelectedIds.filter((id) => !targetIds.includes(id))
            : [...new Set([...baseSelectedIds, ...targetIds])];
        } else {
          nextSelectedIds = getLinkedSectionGroupContainerIds(draft, container);
        }

        // En mode template, sélectionner un container de section liée implique
        // la sélection du groupe complet (même dans une sélection de plage).
        if (draft.currentDocument?.type === 'template') {
          nextSelectedIds = [
            ...new Set(
              nextSelectedIds.flatMap((id) => {
                const c = draft.containers.find(
                  (containerItem) => containerItem.id === id
                );
                return getLinkedSectionGroupContainerIds(draft, c);
              })
            ),
          ];
        }

        setContainerSelection(draft, {
          selectedIds: nextSelectedIds,
          activeId: containerId,
        });
        // En sélection simple de container:
        // on désélectionne la box uniquement si on clique sur un autre container
        if (!ctrlKey && !metaKey && !shiftKey) {
          if (!fromBoxClick && selectedBoxContainerId !== containerId) {
            draft.selectedBoxId = null;
            draft.groupSelection = [];
            draft.boxVariableSelectedId = null;
          }
        }
        if (nextSelectedIds.length > 1) {
          draft.selectedBoxId = null;
          draft.groupSelection = [];
          draft.boxVariableSelectedId = null;
        }
        if (fromBox && (ctrlKey || metaKey || shiftKey)) {
          draft.selectedBoxId = null;
          draft.groupSelection = [];
        }
      }),
      false,
      `Select Container ${typeof payload === 'string' ? payload : payload?.containerId}`
    );
  },
  /**
   * Désigne la cellule sur laquelle le panel de disposition doit s'ouvrir :
   * cliquer une cellule du canvas y déplie le réglage correspondant plutôt que
   * de laisser chercher dans la liste des colonnes.
   *
   * Le nonce fait rejouer l'ouverture quand on reclique la même cellule, par
   * exemple après avoir replié le bloc à la main.
   */
  focusLayoutColumn: (columnId) => {
    if (!columnId) return;
    set(
      (state) => ({
        layoutFocus: {
          columnId,
          nonce: (state.layoutFocus?.nonce ?? 0) + 1,
        },
      }),
      false,
      `focus layout column ${columnId}`
    );
  },
  /**
   * Éteint la cellule désignée, seulement si c'est toujours `columnId` : le
   * panel qui se démonte ne doit pas effacer le focus qu'un clic sur un autre
   * container vient de poser.
   */
  clearLayoutFocus: (columnId) => {
    if (get().layoutFocus?.columnId !== columnId) return;
    set({ layoutFocus: null }, false, `clear layout focus ${columnId}`);
  },
  handleUnSelectContainer: (from = 'anonymous') => {
    if (get().selectedContainer || get().selectedContainerIds?.length > 0) {
      set(
        produce((draft) => {
          setContainerSelection(draft, { selectedIds: [], activeId: null });
          // Sans panel de disposition (section liée, box pleine largeur, mode
          // dessin), rien d'autre n'éteindrait la cellule désignée.
          draft.layoutFocus = null;
        }),
        false,
        `unselectContainer from ${from}`
      );
    }
  },
  removeSectionContainers: (fromBoxId, { sectionId, dateTimestamp }) => {
    // Containers de la section visée (mêmes prédicats que la mutation)
    const sectionContainerIds = get()
      .containers.filter(
        (c) =>
          (fromBoxId && c.fromBoxId === fromBoxId) ||
          (sectionId && dateTimestamp && c.sectionId === sectionId)
      )
      .map((c) => c.id);
    if (denyIfContainerLocked(get(), sectionContainerIds)) return;
    set(
      produce((draft) => {
        mutateRemmoveSectionContainers(draft, fromBoxId, {
          sectionId,
          dateTimestamp,
        });
        setContainerSelection(draft, {
          selectedIds: draft.selectedContainerIds,
          activeId: draft.activeContainerId,
        });
      }),
      false,
      `remove Section Containers ${fromBoxId}`
    );
  },
  removeSelectedContainers: (containerIds = []) => {
    if (denyIfContainerLocked(get(), containerIds)) return;
    set(
      produce((draft) => {
        const uniqueIds = [...new Set(containerIds)].filter((id) =>
          draft.containers.some((c) => c.id === id)
        );
        if (uniqueIds.length === 0) return;

        const selectedContainers = draft.containers.filter((c) =>
          uniqueIds.includes(c.id)
        );

        // Suppression par groupe de section liée (template).
        const linkedGroups = new Map();
        selectedContainers.forEach((container) => {
          if (
            draft.currentDocument?.type === 'template' &&
            container.from_linked_section &&
            container.sectionId &&
            container.dateTimestamp
          ) {
            linkedGroups.set(
              `${container.sectionId}__${container.dateTimestamp}`,
              container
            );
          }
        });

        linkedGroups.forEach((container) => {
          mutateRemmoveSectionContainers(draft, container.fromBoxId, {
            sectionId: container.sectionId,
            dateTimestamp: container.dateTimestamp,
          });
        });

        // Supprime ensuite le reste des containers sélectionnés.
        const idsStillExisting = uniqueIds.filter((id) =>
          draft.containers.some((c) => c.id === id)
        );
        idsStillExisting.forEach((id) => mutateRemoveContainer(draft, id));

        setContainerSelection(draft, { selectedIds: [], activeId: null });
      }),
      false,
      `remove Selected Containers ${containerIds.length}`
    );
  },
  // Toutes les pages d'un même PDF importé, et les masques créés pour ses
  // pages pleine page (cf. createBoxesFromPdfToImage) : sans eux, les masques
  // resteraient orphelins dans la liste des masques. Un seul set : une seule
  // étape d'annulation.
  removePdfImportContainers: (KeyPdfToImage) => {
    if (!KeyPdfToImage) return;
    const containerIds = get()
      .containers.filter((c) => c.KeyPdfToImage === KeyPdfToImage)
      .map((c) => c.id);
    if (denyIfContainerLocked(get(), containerIds)) return;
    set(
      produce((draft) => {
        mutateRemoveContainers(draft, containerIds);
        draft.masks = draft.masks.filter(
          (m) => m.KeyPdfToImage !== KeyPdfToImage
        );
        const selectedBoxStillExists = [
          ...draft.boxes,
          ...draft.masks.flatMap((m) => m.boxes ?? []),
        ].some((b) => b.id === draft.selectedBoxId);
        if (!selectedBoxStillExists) draft.selectedBoxId = null;
        setContainerSelection(draft, { selectedIds: [], activeId: null });
      }),
      false,
      `remove Pdf Import Containers ${KeyPdfToImage}`
    );
  },
  updateContainer: ({ id, fn }) => {
    if (denyIfContainerLocked(get(), id)) return;
    set(
      produce((draft) => {
        const container = draft.containers.find((c) => c.id === id);
        fn(container);
        mutateUpdateContainer(draft, container);
      }),
      false,
      `update Container ${id}`
    );
  },
  // Écart horizontal entre les colonnes du container et de ses lignes
  // imbriquées. Les largeurs de box en dépendent (cf. computeColumnWidth) : on
  // les recale dans la foulée, sinon elles débordent ou laissent un vide.
  setContainerColumnGap: ({ id, columnGap }) => {
    if (denyIfContainerLocked(get(), id)) return;
    set(
      produce((draft) => {
        const container = draft.containers.find((c) => c.id === id);
        if (!container) return;
        container.columnGap = columnGap;
        mutateUpdateContainer(draft, container);
        container.columnsIds.forEach((columnId) =>
          mutateResizeBoxesUnderColumn(draft, columnId)
        );
      }),
      false,
      `set Container ${id} column gap`
    );
  },
  updateColumnsInContainer: ({
    columnsSizes = [],
    index,
    containerId,
    newBoxes = [],
  }) => {
    if (denyIfContainerLocked(get(), containerId)) return;
    set(
      produce((draft) => {
        // containerId peut désigner une ligne imbriquée (cf. rowContainers) :
        // même forme d'objet, mais les largeurs se calculent alors dans la
        // colonne parente et non dans la page.
        const container =
          draft.containers.find((c) => c.id === containerId) ??
          draft.rowContainers.find((c) => c.id === containerId);
        if (!container) return;
        const isRow = !!container.parentColumnId;
        const parentWidthPx = isRow
          ? getColumnAvailableWidth({
              columnId: container.parentColumnId,
              columns: draft.columns,
              containers: draft.containers,
              rowContainers: draft.rowContainers,
              landscape: draft.landscape,
              marginLeft: draft.configuration.margins.left * gridUnit,
              marginRight: draft.configuration.margins.right * gridUnit,
            })
          : undefined;
        const newColsIndex = index === null ? 0 : index + 1;
        const oldColsLength = container.columnsIds.length;
        const newColsLength = columnsSizes.length;
        const colLenghtDiff = newColsLength - oldColsLength;
        const newColSizes = columnsSizes.filter(
          (_, idx) => idx >= newColsIndex && idx < colLenghtDiff + newColsIndex
        );
        const oldColSizes = columnsSizes.filter(
          (_, idx) =>
            !(idx >= newColsIndex && idx < colLenghtDiff + newColsIndex)
        );
        const newColumns = newColSizes.map((size) => newColumn({ size }));
        mutateAddColumns(draft, newColumns);
        const boxesToAdd = [];
        newColumns.forEach((c) => {
          newBoxes.forEach((b) => {
            let boxToAdd = initializeBoxContent({
              box: b,
              configuration: draft.configuration,
              titles: draft.boxes.filter((box) => isATitleBox(box.type)),
              column: c,
              parentId: c.id,
            });
            boxToAdd = reinitializeBoxWidthAndHeight({
              header: draft.masks.find(({ id }) => id === MASKS_TYPE.HEADER.id),
              footer: draft.masks.find(({ id }) => id === MASKS_TYPE.FOOTER.id),
              box: boxToAdd,
              columnWidth: computeColumnWidth({
                landscape: draft.landscape,
                marginLeft: isRow
                  ? 0
                  : draft.configuration.margins.left * gridUnit,
                marginRight: isRow
                  ? 0
                  : draft.configuration.margins.right * gridUnit,
                containerColumnsLength: newColsLength,
                size: c.size,
                parentWidthPx,
                columnGap: containerColumnGap(
                  isRow
                    ? getTopLevelContainer({
                        columnId: container.parentColumnId,
                        containers: draft.containers,
                        rowContainers: draft.rowContainers,
                      })
                    : container
                ),
              }),
              // Comme tous les autres chemins de création (glisser-déposer,
              // « + » d'une cellule, nouveau container) : sans ce drapeau, une
              // box texte gardait sa largeur par défaut — plus étroite qu'une
              // colonne — au lieu de remplir la cellule qu'on vient de créer.
              // Une box collée n'est pas concernée : elle garde la sienne (cf.
              // le garde `!box.copied` dans reinitializeBoxWidthAndHeight).
              onCreate: true,
            });
            boxesToAdd.push(boxToAdd);
          });
        });
        const preexistingColumnIds = [...container.columnsIds];
        // Mutation directe : cf. mutateApplyColumnSizes, mutateUpdateColumn en
        // boucle laisse des proxies révoqués dans draft.columns.
        container.columnsIds.forEach((cId, idx) => {
          const column = draft.columns.find((c) => c.id === cId);
          if (column) column.size = oldColSizes[idx];
        });
        container.columnsIds.splice(
          newColsIndex,
          0,
          ...newColumns.map((c) => c.id)
        );
        mutateAddBoxes({
          draft,
          newBoxes: boxesToAdd,
          drawMode: false,
        });
        // Après le splice : le recalcul lit container.columnsIds.length, qui
        // doit refléter le nouveau nombre de colonnes. Descente récursive car
        // une colonne découpée en lignes ne porte pas de box en direct, ce sont
        // ses sous-colonnes. Limité aux colonnes préexistantes : les nouvelles
        // ont déjà leur largeur (cf. boxesToAdd plus haut), et le splice ne les
        // a pas fait entrer dans les Map de store/lookups.js, cachées par
        // référence de tableau.
        preexistingColumnIds.forEach((cId) =>
          mutateResizeBoxesUnderColumn(draft, cId)
        );

        // La sélection ne connaît que les containers top-level (elle est
        // indexée sur draft.containers, cf. getRangeContainerIds) : pour une
        // ligne, on sélectionne le container qui l'héberge.
        const containerToSelect = isRow
          ? getTopLevelContainer({
              columnId: container.parentColumnId,
              containers: draft.containers,
              rowContainers: draft.rowContainers,
            })
          : container;
        if (containerToSelect) {
          mutateDropInContainer({
            draft,
            container: containerToSelect,
            box: boxesToAdd[0],
          });
        }
      }),
      false,
      `add ${columnsSizes.length} in container ${containerId}`
    );
  },
  /**
   * Applique une disposition complète à un container ou à une ligne : ajoute,
   * retire et redimensionne les colonnes pour coller exactement à
   * `columnsSizes`. Complète updateColumnsInContainer, qui ne sait qu'ajouter.
   * Les colonnes retirées (les dernières) partent avec leur contenu.
   */
  applyContainerDisposition: ({ containerId, columnsSizes = [] }) => {
    if (denyIfContainerLocked(get(), containerId)) return;
    set(
      produce((draft) => {
        mutateApplyColumnSizes(draft, { containerId, columnsSizes });
      }),
      false,
      `applyContainerDisposition ${containerId}`
    );
  },
  /**
   * Applique une disposition complète incluant les lignes : répartition des
   * colonnes du container, puis pour chacune le nombre de lignes empilées
   * voulu. Sert aux presets « grand bloc + pile » et compagnie.
   * `layout` : [{ size, rows }, ...]
   */
  applyContainerLayout: ({ containerId, layout = [] }) => {
    if (denyIfContainerLocked(get(), containerId)) return;
    set(
      produce((draft) => {
        const container = draft.containers.find((c) => c.id === containerId);
        if (!container || !layout.length) return;
        mutateApplyColumnSizes(draft, {
          containerId,
          columnsSizes: layout.map((l) => l.size),
        });
        container.columnsIds.forEach((columnId, idx) => {
          mutateSetColumnRows(draft, {
            columnId,
            rowCount: layout[idx]?.rows ?? 1,
          });
        });
      }),
      false,
      `applyContainerLayout ${containerId}`
    );
  },
  /**
   * Fixe le nombre de lignes empilées d'une colonne (réglages avancés).
   * 0 ou 1 ramène la colonne à une colonne simple.
   */
  setColumnRowCount: ({ columnId, rowCount }) => {
    if (denyIfColumnLocked(get(), columnId)) return;
    set(
      produce((draft) => {
        mutateSetColumnRows(draft, { columnId, rowCount });
      }),
      false,
      `setColumnRowCount ${columnId} ${rowCount}`
    );
  },
  /**
   * Découpe une colonne simple en une seule ligne de sous-colonnes (réglages
   * avancés). Sans ça, il fallait passer à 2 lignes pour voir apparaître le
   * réglage des sous-colonnes. Le contenu direct part dans la première.
   */
  splitColumnIntoSubColumns: ({ columnId, columnsSizes }) => {
    if (denyIfColumnLocked(get(), columnId)) return;
    set(
      produce((draft) => {
        const column = draft.columns.find((c) => c.id === columnId);
        if (!column || column.drawMode || column.rowContainerIds?.length) return;
        const { row, columns } = createRowInColumn(draft, {
          parentColumnId: columnId,
          columnsSizes,
        });
        draft.boxes.forEach((b) => {
          if (b.columnId === columnId) b.columnId = columns[0].id;
        });
        column.rowContainerIds = [row.id];
        mutateResizeBoxesUnderColumn(draft, columnId);
      }),
      false,
      `splitColumnIntoSubColumns ${columnId}`
    );
  },
  addRowToColumn: ({ columnId, columnsSizes = [1], index }) => {
    if (denyIfColumnLocked(get(), columnId)) return;
    if (denyIfLinkedSection(get(), columnId)) return;
    set(
      produce((draft) => {
        const column = draft.columns.find((c) => c.id === columnId);
        if (!column || column.drawMode) return;
        // Colonne simple : son contenu descend d'abord dans une première ligne,
        // la nouvelle se pose dessous (« + » sous une colonne de container).
        if (!column.rowContainerIds?.length) {
          const { row: firstRow, columns } = createRowInColumn(draft, {
            parentColumnId: columnId,
            columnsSizes: [1],
          });
          draft.boxes.forEach((b) => {
            if (b.columnId === columnId) b.columnId = columns[0].id;
          });
          column.rowContainerIds = [firstRow.id];
          mutateResizeBoxesUnderColumn(draft, columnId);
        }
        const { row } = createRowInColumn(draft, {
          parentColumnId: columnId,
          columnsSizes,
        });
        const at =
          typeof index === 'number' ? index + 1 : column.rowContainerIds.length;
        column.rowContainerIds.splice(at, 0, row.id);
      }),
      false,
      `addRowToColumn ${columnId}`
    );
  },
  removeRowFromColumn: ({ columnId, rowContainerId }) => {
    if (denyIfColumnLocked(get(), columnId)) return;
    if (denyIfLinkedSection(get(), columnId)) return;
    set(
      produce((draft) => {
        const column = draft.columns.find((c) => c.id === columnId);
        if (!column) return;
        // Retirer la référence AVANT la cascade : mutateRemoveRowContainers ne
        // nettoie pas les rowContainerIds du parent (même contrat que
        // deleteColumns / container.columnsIds).
        column.rowContainerIds = (column.rowContainerIds ?? []).filter(
          (id) => id !== rowContainerId
        );
        mutateRemoveRowContainers(draft, [rowContainerId]);
      }),
      false,
      `removeRowFromColumn ${rowContainerId}`
    );
  },
  /**
   * Retire une sous-colonne précise d'une ligne, avec son contenu. Les autres
   * se partagent sa place en gardant leurs proportions. La dernière
   * sous-colonne ne se retire pas ainsi : c'est alors la ligne qu'on supprime.
   */
  removeColumnFromRow: ({ rowContainerId, columnId }) => {
    if (denyIfColumnLocked(get(), columnId)) return;
    if (denyIfLinkedSection(get(), columnId)) return;
    set(
      produce((draft) => {
        const row = draft.rowContainers?.find((r) => r.id === rowContainerId);
        if (!row || row.columnsIds.length < 2) return;
        if (!row.columnsIds.includes(columnId)) return;
        const sizeOf = (cId) =>
          draft.columns.find((c) => c.id === cId)?.size ?? 0;
        // Total d'origine plutôt que 1 : les dispositions à trois colonnes
        // valent 0.33 chacune, la ligne ne doit ni grandir ni rétrécir.
        const total = row.columnsIds.reduce((sum, cId) => sum + sizeOf(cId), 0);
        row.columnsIds = row.columnsIds.filter((id) => id !== columnId);
        mutateDeleteColumns(draft, [columnId]);
        const remainingTotal = row.columnsIds.reduce(
          (sum, cId) => sum + sizeOf(cId),
          0
        );
        // Mutation directe : cf. mutateApplyColumnSizes.
        row.columnsIds.forEach((cId) => {
          const column = draft.columns.find((c) => c.id === cId);
          if (!column) return;
          column.size =
            remainingTotal > 0
              ? (column.size * total) / remainingTotal
              : total / row.columnsIds.length;
        });
        row.columnsIds.forEach((cId) =>
          mutateResizeBoxesUnderColumn(draft, cId)
        );
        if (draft.layoutFocus?.columnId === columnId) draft.layoutFocus = null;
      }),
      false,
      `removeColumnFromRow ${columnId}`
    );
  },
  /**
   * Vide une colonne de container : ses box partent, la colonne reste en place
   * (pendant du « × » de ligne, qui vide la cellule sans toucher aux voisines).
   * La suppression de la colonne elle-même passe par la barre de colonne.
   */
  clearColumn: (columnId) => {
    if (denyIfColumnLocked(get(), columnId)) return;
    if (denyIfLinkedSection(get(), columnId)) return;
    set(
      produce((draft) => {
        const boxIds = draft.boxes
          .filter((b) => b.columnId === columnId)
          .map((b) => b.id);
        boxIds.forEach((boxId) => mutateRemoveBox(draft, boxId));
        if (boxIds.includes(draft.selectedBoxId)) draft.selectedBoxId = null;
      }),
      false,
      `clearColumn ${columnId}`
    );
  },
  /**
   * Touche Entrée dans une box texte : ce qui suit le curseur part dans une
   * nouvelle box texte créée juste en dessous, qui prend le focus.
   *
   * « En dessous » se lit au niveau d'empilement le plus proche du curseur :
   * - box dans une ligne imbriquée → nouvelle ligne juste après elle ;
   * - box dans une colonne d'un container multi-colonnes → la colonne devient
   *   une pile de lignes (la box existante descend dans la première). Créer un
   *   container en dessous pousserait le texte sous TOUT le bloc multi-colonnes ;
   * - box dans la colonne unique d'un container → nouveau container frère, et
   *   pas une ligne : le service PDF reconvertit précisément ce cas en
   *   containers frères (cf. flattenMainRows), autant produire la structure
   *   plate tout de suite — pagination gloutonne libre, pas de tranchage.
   *
   * Retourne false si la création est impossible (verrou, section liée, mode
   * dessin, type non géré) : l'appelant retombe alors sur le retour ligne.
   */
  splitTextBoxAtCaret: ({ boxId, headEditorState, tailEditorState }) => {
    const state = get();
    const box = state.boxes.find((b) => b.id === boxId);
    // TEXT seulement : dupliquer une box variable dupliquerait son identité de
    // variable, hors sujet pour cette touche.
    if (!box || box.type !== ItemTypes.TEXT) return false;

    const column = getColumnById(state.columns, box.columnId);
    if (!column || column.drawMode) return false;

    const freeze = editFreezeHolder(state);
    if (freeze) {
      message.warning(freeze.message);
      return false;
    }

    const topLevelContainer = getTopLevelContainer({
      columnId: box.columnId,
      containers: state.containers,
      rowContainers: state.rowContainers,
    });
    if (!topLevelContainer) return false;
    // Section liée : insérer au milieu du groupe le casserait.
    if (topLevelContainer.from_linked_section) return false;
    if (denyIfContainerLocked(state, topLevelContainer.id)) return false;

    const parentRow = getContainerByColumnId(
      state.rowContainers ?? [],
      box.columnId
    );
    const isSingleColumn = topLevelContainer.columnsIds.length === 1;
    // Colonne déjà porteuse de lignes mais box en direct : état incohérent, on
    // ne tente pas de le rattraper ici.
    if (!parentRow && !isSingleColumn && column.rowContainerIds?.length)
      return false;

    // Colonne porteuse de la ligne courante : sans elle, pas d'endroit où
    // insérer la ligne suivante.
    if (parentRow && !getColumnById(state.columns, parentRow.parentColumnId))
      return false;

    const containerIndex = state.containers.findIndex(
      (c) => c.id === topLevelContainer.id
    );
    // Clone : la nouvelle box hérite du style de texte, de l'alignement, de la
    // largeur et des bordures de celle qu'on quitte. La hauteur suit le contenu
    // pour un texte (AUTO_HEIGHT_BOXES), sa valeur ici n'a pas d'incidence.
    const newBox = {
      ...box,
      id: uuid(),
      content: {
        ...box.content,
        text: '',
        editorState: tailEditorState ?? EditorState.createEmpty(),
      },
    };

    set(
      produce((draft) => {
        // mutateAddContainers pose lui-même la sélection sur la nouvelle box.
        let selectNewBox = true;
        let inserted = false;

        if (parentRow) {
          const parentColumn = draft.columns.find(
            (c) => c.id === parentRow.parentColumnId
          );
          const { row, columns } = createRowInColumn(draft, {
            parentColumnId: parentColumn.id,
            columnsSizes: [1],
          });
          mutateAddBoxes({
            draft,
            newBoxes: [{ ...newBox, columnId: columns[0].id }],
          });
          parentColumn.rowContainerIds.splice(
            parentColumn.rowContainerIds.indexOf(parentRow.id) + 1,
            0,
            row.id
          );
          inserted = true;
        } else if (isSingleColumn) {
          mutateAddContainers({
            draft,
            index: containerIndex,
            count: 1,
            columnsSizes: [1],
            box: newBox,
          });
          selectNewBox = false;
          inserted = true;
        } else {
          // La colonne devient une pile de deux lignes : la box source descend
          // dans la première, la nouvelle prend la seconde.
          mutateSetColumnRows(draft, { columnId: box.columnId, rowCount: 2 });
          const draftColumn = draft.columns.find((c) => c.id === box.columnId);
          const secondRow = draft.rowContainers.find(
            (r) => r.id === draftColumn?.rowContainerIds?.[1]
          );
          if (secondRow) {
            mutateAddBoxes({
              draft,
              newBoxes: [{ ...newBox, columnId: secondRow.columnsIds[0] }],
            });
            inserted = true;
          }
        }

        // Troncature en dernier : rien ne doit amputer la box source si la
        // nouvelle n'a pas trouvé sa place, sinon la queue est perdue.
        if (!inserted) return;
        draft.boxes.find((b) => b.id === boxId).content.editorState =
          headEditorState;

        if (selectNewBox) {
          mutateDropInContainer({
            draft,
            container: draft.containers.find(
              (c) => c.id === topLevelContainer.id
            ),
            box: newBox,
          });
        }
      }),
      false,
      `splitTextBoxAtCaret ${boxId}`
    );

    // Les deux chemins d'insertion posent la sélection sur la nouvelle box.
    if (get().selectedBoxId !== newBox.id) return false;

    // La box source a été tronquée dans le commit qui vient de changer la
    // sélection. Sans ce drapeau, la sauvegarde à la déselection de RichEditor
    // (index.jsx, cleanup du useLayoutEffect) réécrirait le contenu ENTIER : ce
    // cleanup lit une ref posée par un effet passif, donc en retard d'un commit.
    // Posé ici, avant le rendu que zustand déclenche à la sortie du gestionnaire.
    get().addSkipBoxSaveOnUnselect(boxId);

    if (get().previewRenderState) get().appendPages(containerIndex);
    return true;
  },
  setDisplayMargins: () => {
    set(
      produce((draft) => {
        draft.displayMargins = !draft.displayMargins;
      }),
      false,
      'setDisplayMargins'
    );
  },
  setFirstVisibleContainerId: (containerId) => {
    if (containerId === get().firstVisibleContainerId) return;
    set(
      produce((draft) => {
        draft.firstVisibleContainerId = containerId;
      }),
      false,
      `setFirstVisibleContainerId ${containerId || 'null'}`
    );
  },
});
